import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { useCallbackRef } from '@/hooks/useCallbackRef';
import { afterFrames } from '@/lib/animate';
import {
  clearHighlights,
  highlightMatches,
  type HighlightMode,
} from '@/lib/highlight-matches';
import { toCaseInsensitiveSearchText } from '@/lib/unicode-search';

import { useDebouncedCallback } from './useDebouncedCallback';

import type { VirtualizerHandle } from 'virtua';

const hotkeyConfig = {
  preventDefault: false,
  enableOnFormTags: true,
  enableOnContentEditable: true,
} as const;

const MIN_SCROLL_HIGHLIGHT_INTERVAL_MS = 50;

const mountedFindContainers = new Set<HTMLElement>();
let hoveredFindContainer: HTMLElement | null = null;

type SearchResult = {
  itemIndex: number;
  occurrenceIndex: number;
  fieldKey?: string;
};

interface FocusSearchOptions {
  query: string;
  occurrence?: number;
  itemIndex?: number;
  caseSensitive?: boolean;
  highlightMode?: HighlightMode;
  clearAfterMs?: number;
  onResult?: (didFocus: boolean) => void;
}

interface UseVirtualListFindOptions {
  vlistRef: React.RefObject<VirtualizerHandle | null>;
  containerRef: React.RefObject<HTMLElement | null>;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  fieldsOnly?: boolean;
  onNavigate?: (result: SearchResult) => void;
  /**
   * Return every occurrence across all items.
   * The hook owns orchestration; the provider owns search.
   */
  search: (query: string, caseSensitive: boolean) => SearchResult[];
  /** DOM id prefix for list items */
  itemIdPrefix: string;
  /** Scope for Cmd/Ctrl+F handling */
  hotkeyScope?: 'active' | 'global';
  listIndexOf?: (itemIndex: number) => number;
}

type UseVirtualListFindReturn = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  query: string;
  setQuery: (q: string) => void;
  caseSensitive: boolean;
  setCaseSensitive: (v: boolean) => void;
  currentMatch: number;
  totalMatches: number;
  goToNext: () => void;
  goToPrevious: () => void;
  handleScroll: () => void;
  focus: (options: FocusSearchOptions) => boolean;
};

export function scrollRangeIntoView(range: Range, itemEl: HTMLElement) {
  // Reveal the match inside a scrolling card body before scrolling the list
  let scrollEl: HTMLElement | null =
    range.startContainer.parentElement ?? itemEl;
  while (scrollEl) {
    const { overflowY } = getComputedStyle(scrollEl);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      scrollEl.scrollHeight > scrollEl.clientHeight
    ) {
      const rangeRect = range.getBoundingClientRect();
      const scrollRect = scrollEl.getBoundingClientRect();
      if (
        rangeRect.top < scrollRect.top ||
        rangeRect.bottom > scrollRect.bottom
      ) {
        scrollEl.scrollTo({
          top:
            scrollEl.scrollTop +
            rangeRect.top -
            scrollRect.top -
            scrollRect.height / 2,
        });
      }
    }
    scrollEl = scrollEl.parentElement;
  }
}

/** Default search provider: indexOf scan over a list of text items */
export function searchItems(
  items: {
    length: number;
    getText: (i: number) => string;
    /** Optional pre-folded text from toCaseInsensitiveSearchText */
    getNormalizedText?: (i: number) => string;
  },
  query: string,
  caseSensitive: boolean
): SearchResult[] {
  if (!query) return [];
  const results: SearchResult[] = [];
  const q = caseSensitive ? query : toCaseInsensitiveSearchText(query);
  if (!q) return [];
  const getSearchText = caseSensitive
    ? items.getText
    : items.getNormalizedText
      ? items.getNormalizedText
      : (i: number) => toCaseInsensitiveSearchText(items.getText(i));

  for (let i = 0; i < items.length; i++) {
    const text = getSearchText(i);
    let occurrenceIndex = 0;
    let startPos = 0;
    while (startPos < text.length) {
      const idx = text.indexOf(q, startPos);
      if (idx === -1) break;
      results.push({ itemIndex: i, occurrenceIndex });
      occurrenceIndex++;
      startPos = idx + q.length;
    }
  }
  return results;
}

export function useVirtualListFind({
  vlistRef,
  containerRef,
  inputRef,
  fieldsOnly = false,
  onNavigate,
  search,
  itemIdPrefix,
  hotkeyScope = 'global',
  listIndexOf,
}: UseVirtualListFindOptions): UseVirtualListFindReturn {
  const toListIndex = useCallbackRef(listIndexOf);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQueryRaw] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [currentMatch, setCurrentMatch] = useState(0);

  const pendingScrollRef = useRef(false);
  const highlightRafRef = useRef<number | null>(null);
  const lastScrollHighlightAtRef = useRef(0);
  const searchGenRef = useRef(0);
  const focusClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const getResultElement = useCallback(
    (result: SearchResult): HTMLElement | null => {
      const item = document.getElementById(
        `${itemIdPrefix}-${result.itemIndex}`
      );
      return result.fieldKey
        ? (item?.querySelector<HTMLElement>(
            `[data-find-field="${CSS.escape(result.fieldKey)}"]`
          ) ?? null)
        : item;
    },
    [itemIdPrefix]
  );

  const getCurrentResult = useCallback((): {
    element: HTMLElement | null;
    occurrenceIndex: number;
  } => {
    if (results.length === 0) return { element: null, occurrenceIndex: 0 };
    const result = results[currentMatch];
    if (!result) return { element: null, occurrenceIndex: 0 };
    return {
      element: getResultElement(result),
      occurrenceIndex: result.occurrenceIndex,
    };
  }, [results, currentMatch, getResultElement]);

  const clearFocusClearTimeout = useCallback(() => {
    if (focusClearTimeoutRef.current === null) return;
    clearTimeout(focusClearTimeoutRef.current);
    focusClearTimeoutRef.current = null;
  }, []);

  const scheduleFocusClear = useCallback(
    (clearAfterMs?: number) => {
      clearFocusClearTimeout();
      if (!clearAfterMs || clearAfterMs <= 0) return;
      focusClearTimeoutRef.current = setTimeout(() => {
        focusClearTimeoutRef.current = null;
        searchGenRef.current++;
        setQueryRaw('');
        setResults([]);
        setCurrentMatch(0);
        clearHighlights(itemIdPrefix);
      }, clearAfterMs);
    },
    [clearFocusClearTimeout, itemIdPrefix]
  );

  const runHighlight = useCallback(
    (highlightMode: HighlightMode = 'all') => {
      const container = containerRef.current;
      if (!container || !query) return;
      const { element, occurrenceIndex } = getCurrentResult();
      highlightMatches(
        container,
        query,
        element,
        caseSensitive,
        occurrenceIndex,
        itemIdPrefix,
        { highlightMode, fieldsOnly }
      );
    },
    [
      containerRef,
      query,
      caseSensitive,
      getCurrentResult,
      itemIdPrefix,
      fieldsOnly,
    ]
  );

  const scrollToResult = useCallback(
    (
      result: SearchResult,
      options?: {
        query?: string;
        caseSensitive?: boolean;
        highlightMode?: HighlightMode;
        onResult?: (didFocus: boolean) => void;
      }
    ) => {
      onNavigate?.(result);
      const handle = vlistRef.current;
      let didSettle = false;

      const settle = (didFocus: boolean) => {
        if (didSettle) return;
        didSettle = true;
        options?.onResult?.(didFocus);
      };

      if (!handle) {
        settle(false);
        return;
      }

      const queryText = options?.query ?? query;
      const caseSensitiveSearch = options?.caseSensitive ?? caseSensitive;
      const highlightMode = options?.highlightMode ?? 'all';
      const gen = ++searchGenRef.current;

      const notifyFocus = () => {
        settle(true);
      };

      pendingScrollRef.current = true;
      handle.scrollToIndex(toListIndex(result.itemIndex) ?? result.itemIndex, {
        align: 'center',
      });

      let attempts = 0;
      const maxAttempts = 10;

      const tryHighlight = () => {
        if (searchGenRef.current !== gen) {
          settle(false);
          return;
        }

        attempts++;
        const container = containerRef.current;
        const itemEl = getResultElement(result);

        if (container && queryText && itemEl) {
          const { currentRange } = highlightMatches(
            container,
            queryText,
            itemEl,
            caseSensitiveSearch,
            result.occurrenceIndex,
            itemIdPrefix,
            { highlightMode, fieldsOnly }
          );

          if (fieldsOnly && !currentRange && attempts < maxAttempts) {
            requestAnimationFrame(tryHighlight);
            return;
          }
          if (currentRange) {
            scrollRangeIntoView(currentRange, itemEl);
          }

          pendingScrollRef.current = false;
          // Repaint after late-rendering components (e.g. syntax-highlighted
          // code blocks) that may re-render and invalidate highlight ranges
          afterFrames(10, () => {
            if (searchGenRef.current !== gen) {
              settle(false);
              return;
            }
            const repaintResult = highlightMatches(
              container,
              queryText,
              itemEl,
              caseSensitiveSearch,
              result.occurrenceIndex,
              itemIdPrefix,
              { highlightMode, fieldsOnly }
            );
            if (repaintResult.currentRange) {
              if (fieldsOnly)
                scrollRangeIntoView(repaintResult.currentRange, itemEl);
              notifyFocus();
              return;
            }
            settle(false);
          });
          return;
        }

        if (attempts < maxAttempts) {
          requestAnimationFrame(tryHighlight);
          return;
        }

        pendingScrollRef.current = false;
        runHighlight(highlightMode);
        settle(false);
      };

      requestAnimationFrame(tryHighlight);
    },
    [
      vlistRef,
      containerRef,
      toListIndex,
      query,
      caseSensitive,
      itemIdPrefix,
      runHighlight,
      getResultElement,
      fieldsOnly,
      onNavigate,
    ]
  );

  const executeSearch = useDebouncedCallback((q: string, cs: boolean) => {
    if (!q) {
      setResults([]);
      setCurrentMatch(0);
      clearHighlights(itemIdPrefix);
      return;
    }

    const searchResults = search(q, cs);
    setResults(searchResults);
    setCurrentMatch(0);

    if (searchResults.length > 0) {
      scrollToResult(searchResults[0]);
    } else {
      clearHighlights(itemIdPrefix);
    }
  }, 200);

  const setQuery = useCallback(
    (q: string) => {
      clearFocusClearTimeout();
      searchGenRef.current++;
      setQueryRaw(q);
      if (!q) {
        executeSearch.cancel();
        setResults([]);
        setCurrentMatch(0);
        clearHighlights(itemIdPrefix);
        return;
      }
      executeSearch(q, caseSensitive);
    },
    [clearFocusClearTimeout, executeSearch, caseSensitive, itemIdPrefix]
  );

  const focus = useCallback(
    ({
      query: focusQuery,
      occurrence = 0,
      itemIndex,
      caseSensitive: focusCaseSensitive = caseSensitive,
      highlightMode = 'all',
      clearAfterMs,
      onResult,
    }: FocusSearchOptions) => {
      clearFocusClearTimeout();
      searchGenRef.current++;
      executeSearch.cancel();
      setQueryRaw(focusQuery);

      if (!focusQuery) {
        setResults([]);
        setCurrentMatch(0);
        clearHighlights(itemIdPrefix);
        onResult?.(false);
        return false;
      }

      const searchResults = search(focusQuery, focusCaseSensitive);
      setResults(searchResults);

      if (searchResults.length === 0) {
        setCurrentMatch(0);
        clearHighlights(itemIdPrefix);
        onResult?.(false);
        return false;
      }

      const requestedOccurrence = Math.max(occurrence, 0);

      let targetResultIndex = 0;
      if (itemIndex !== undefined) {
        const targetResult = searchResults.find(
          (result) =>
            result.itemIndex === itemIndex &&
            result.occurrenceIndex === requestedOccurrence
        );

        if (!targetResult) {
          onResult?.(false);
          return false;
        }

        targetResultIndex = searchResults.indexOf(targetResult);
      } else {
        targetResultIndex = Math.min(
          requestedOccurrence,
          searchResults.length - 1
        );
      }

      setCurrentMatch(targetResultIndex);
      scrollToResult(searchResults[targetResultIndex], {
        query: focusQuery,
        caseSensitive: focusCaseSensitive,
        highlightMode,
        onResult: (didFocus) => {
          if (didFocus && highlightMode === 'current') {
            scheduleFocusClear(clearAfterMs);
          }
          onResult?.(didFocus);
        },
      });

      return true;
    },
    [
      caseSensitive,
      clearFocusClearTimeout,
      executeSearch,
      itemIdPrefix,
      scheduleFocusClear,
      scrollToResult,
      search,
    ]
  );

  useEffect(() => {
    if (!isOpen || !query) return;
    executeSearch(query, caseSensitive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseSensitive, search, isOpen]);

  const goToNext = useCallback(() => {
    clearFocusClearTimeout();
    if (results.length === 0) return;
    const next = (currentMatch + 1) % results.length;
    setCurrentMatch(next);
    scrollToResult(results[next]);
  }, [clearFocusClearTimeout, results, currentMatch, scrollToResult]);

  const goToPrevious = useCallback(() => {
    clearFocusClearTimeout();
    if (results.length === 0) return;
    const prev = (currentMatch - 1 + results.length) % results.length;
    setCurrentMatch(prev);
    scrollToResult(results[prev]);
  }, [clearFocusClearTimeout, results, currentMatch, scrollToResult]);

  const open = useCallback(() => {
    clearFocusClearTimeout();
    setIsOpen(true);
    inputRef?.current?.focus();
  }, [clearFocusClearTimeout, inputRef]);

  const invalidateSearch = useCallback(() => {
    searchGenRef.current++;
  }, []);

  const close = useCallback(() => {
    clearFocusClearTimeout();
    invalidateSearch();
    executeSearch.cancel();
    setIsOpen(false);
    setQueryRaw('');
    setResults([]);
    setCurrentMatch(0);
    clearHighlights(itemIdPrefix);
    lastScrollHighlightAtRef.current = 0;
  }, [clearFocusClearTimeout, executeSearch, itemIdPrefix, invalidateSearch]);

  const handleScroll = useCallback(() => {
    if (!query || !isOpen || pendingScrollRef.current) return;
    if (
      performance.now() - lastScrollHighlightAtRef.current <
      MIN_SCROLL_HIGHLIGHT_INTERVAL_MS
    ) {
      return;
    }
    if (highlightRafRef.current !== null) return;
    highlightRafRef.current = requestAnimationFrame(() => {
      highlightRafRef.current = null;
      lastScrollHighlightAtRef.current = performance.now();
      runHighlight();
    });
  }, [query, isOpen, runHighlight]);

  useEffect(() => {
    return () => {
      invalidateSearch();
      clearFocusClearTimeout();
      clearHighlights(itemIdPrefix);
      if (highlightRafRef.current !== null) {
        cancelAnimationFrame(highlightRafRef.current);
      }
    };
  }, [clearFocusClearTimeout, itemIdPrefix, invalidateSearch]);

  // Safety net: re-highlight when results change without a scroll event
  // (e.g. first match already in viewport, scrollToIndex is a no-op)
  useEffect(() => {
    if (!isOpen || !query || results.length === 0 || pendingScrollRef.current)
      return;
    const frame = requestAnimationFrame(() => runHighlight());
    return () => cancelAnimationFrame(frame);
  }, [isOpen, query, results, runHighlight, itemIdPrefix]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    mountedFindContainers.add(container);

    const onEnter = () => (hoveredFindContainer = container);
    const onLeave = () => {
      if (hoveredFindContainer === container) hoveredFindContainer = null;
    };
    container.addEventListener('pointerenter', onEnter);
    container.addEventListener('pointerleave', onLeave);

    return () => {
      mountedFindContainers.delete(container);
      if (hoveredFindContainer === container) hoveredFindContainer = null;
      container.removeEventListener('pointerenter', onEnter);
      container.removeEventListener('pointerleave', onLeave);
    };
  }, [containerRef]);

  /**
   * When multiple find-enabled lists coexist, Cmd+F priority is:
   * 1. Focused container (contains document.activeElement)
   * 2. Hovered container
   * 3. First mounted container (only when hotkeyScope is "global")
   */
  useHotkeys(
    'mod+f',
    (event) => {
      const container = containerRef.current;
      if (!container) return;

      // 1. Focused container wins
      const focused = document.activeElement;
      if (focused instanceof Node) {
        for (const c of mountedFindContainers) {
          if (c.contains(focused)) {
            if (c === container) {
              event.preventDefault();
              open();
            }
            return;
          }
        }
      }

      // 2. Hovered container
      if (hoveredFindContainer) {
        if (hoveredFindContainer === container) {
          event.preventDefault();
          open();
        }
        return;
      }

      // 3. First mounted (opt-in fallback)
      if (
        hotkeyScope === 'global' &&
        mountedFindContainers.values().next().value === container
      ) {
        event.preventDefault();
        open();
      }
    },
    hotkeyConfig,
    [containerRef, hotkeyScope, open]
  );
  useHotkeys('escape', () => close(), { ...hotkeyConfig, enabled: isOpen });

  return {
    isOpen,
    open,
    close,
    query,
    setQuery,
    caseSensitive,
    setCaseSensitive,
    currentMatch,
    totalMatches: results.length,
    goToNext,
    goToPrevious,
    handleScroll,
    focus,
  };
}
